import { PlatformApiResultOfUserInfoDto } from '@data/_generated-api/platform-api/models/platform-api-result-of-user-info-dto';
import { Observable, Subject } from 'rxjs';
import { BroadcastChannel } from 'broadcast-channel';
import { generateRandomId } from '@common/utils/string-utils';

export const tabsKey = 'tabs';
const authKey = 'auth';
const authSavedAtKey = 'authSavedAt';
const channelKey = 'tabs';

export class CurrentTab implements SessionTab {
  private readonly _id = generateRandomId();
  private readonly _channel = new BroadcastChannel(channelKey);

  private _tabs: SessionTab[] | undefined = undefined;
  private _auth: PlatformApiResultOfUserInfoDto | undefined = undefined;

  constructor() {}

  private readonly _messageFromOtherTab$ = new Subject<TabMessageKey>();
  public get messageFromOtherTab$(): Observable<TabMessageKey> {
    return this._messageFromOtherTab$;
  }

  private readonly _logout$ = new Subject<undefined>();
  get logout$(): Observable<undefined> {
    return this._logout$;
  }

  private readonly _login$ = new Subject<undefined>();
  get login$(): Observable<undefined> {
    return this._login$;
  }

  get id(): string {
    return this._id;
  }

  get isLeading(): boolean {
    return this.getActiveTab()?.id === this._id;
  }

  init(): void {
    this.initStorageListeners();
    this.initChannelListeners();
    const tabs = this.getTabs();
    const activeTab = this.getActiveTab();

    tabs.push({
      id: this._id,
      isLeading: !activeTab
    });
    this.save(tabs);

    this.checkTabsStilOpened();
  }

  makeLeading(): void {
    const tabs = this.getTabs();
    tabs.forEach(t => (t.isLeading = false));
    const index = tabs.findIndex(t => t.id === this._id);
    if (index > -1) {
      tabs[index].isLeading = true;
      this.save(tabs);
    }
  }

  saveAuth(result: PlatformApiResultOfUserInfoDto): void {
    const serial = JSON.stringify(result);
    localStorage.setItem(authKey, serial);
    localStorage.setItem(authSavedAtKey, String(Date.now()));
    this._auth = result;
  }

  clearAuth(): void {
    localStorage.removeItem(authKey);
    localStorage.removeItem(authSavedAtKey);
    this._auth = undefined;
  }

  getAuth(): PlatformApiResultOfUserInfoDto | undefined {
    if (this._auth) {
      return this._auth;
    }

    const serial = localStorage.getItem(authKey);
    if (!serial) {
      return undefined;
    }

    return JSON.parse(serial);
  }

  /** Moment (epoch ms) waarop saveAuth het laatst draaide; voor het herbruik-venster van de refresher. */
  getAuthSavedAt(): number | undefined {
    const serial = localStorage.getItem(authSavedAtKey);
    if (!serial) {
      return undefined;
    }

    const savedAt = Number(serial);
    return Number.isFinite(savedAt) ? savedAt : undefined;
  }

  getUseMultipleTabs(): boolean {
    const isDevice = localStorage.getItem('isDevice') === 'true';
    if (isDevice) {
      return false;
    }

    return !!JSON.parse(localStorage.getItem('multipleTabs'));
  }

  getTabs(): SessionTab[] {
    if (this._tabs) {
      return this._tabs;
    }

    const tabsSerial = localStorage.getItem(tabsKey);
    return tabsSerial ? JSON.parse(tabsSerial) : [];
  }

  getActiveTab(): SessionTab | undefined {
    return this.getTabs().find(t => t.isLeading);
  }

  destroy() {
    const tabs = this.getTabs();
    const index = tabs.findIndex(t => t.id === this._id);

    if (index === -1) {
      return;
    }

    const [removed] = tabs.splice(index, 1);

    // Alleen promoveren wanneer de leidende tab zelf sluit; anders ontstaan er
    // twee entries met isLeading en dus split-brain tussen tabs.
    if (removed.isLeading && tabs.length) {
      tabs[0].isLeading = true;
    }

    this.save(tabs);
  }

  notify(key: TabMessageKey): void {
    this._channel.postMessage(key);
  }

  private save(tabs: SessionTab[]) {
    const tabsToSave = tabs.map(({ id, isLeading }) => ({ id, isLeading }));
    localStorage.setItem(tabsKey, JSON.stringify(tabsToSave));
  }

  private initStorageListeners(): void {
    window.addEventListener('storage', e => {
      if (e.key === tabsKey) {
        this._tabs = JSON.parse(e.newValue);
      }

      if (e.key === authKey) {
        this._auth = e.newValue ? JSON.parse(e.newValue) : undefined;
      }
    });
  }

  private initChannelListeners(): void {
    this._channel.onmessage = ev => {
      const message = ev as TabMessageKey;

      this._messageFromOtherTab$.next(message);

      if (message === 'logout') {
        this._logout$.next(undefined);
      }

      if (message === 'login') {
        this._login$.next(undefined);
      }

      if (message === 'init') {
        const key: TabMessageKey = 'active:' + this.id;
        this.notify(key);
      }

      if (message.includes('active:')) {
        const id = message.replace('active:', '');
        this._activeIds.push(id);
      }
    };
  }

  private _activeIds: string[] = [];

  private checkTabsStilOpened(): void {
    this.notify('init');

    setTimeout(() => {
      const tabs = this.getTabs();
      const activeTabs = tabs.filter(t => t.id === this.id || this._activeIds.includes(t.id));

      this.save(activeTabs);

      if (!activeTabs.some(t => t.isLeading)) {
        this.makeLeading();
      }
      this._activeIds = [];
    }, 1000);
  }
}

export const currentTab = new CurrentTab();

export interface SessionTab {
  id: string;
  isLeading: boolean;
}

export type TabMessageKey =
  | ('logout' | 'login' | 'init' | `active:${string}` | `update:${'dismiss' | 'restart'}`)
  | string;
